import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle, ChevronDown, ChevronUp, ListChecks, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

export default function SetupChecklist() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("bfa_checklist_collapsed") === "1");

  useEffect(() => {
    api.get("/onboarding/checklist").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return null;
  if (data.checklist_hidden && localStorage.getItem("bfa_show_checklist") !== "1") return null;

  const toggle = () => {
    const v = !collapsed;
    setCollapsed(v);
    localStorage.setItem("bfa_checklist_collapsed", v ? "1" : "0");
  };
  const incomplete = data.status !== "complete";

  return (
    <div className="rounded-xl border border-brand-amber/30 bg-brand-amber/5 p-5" data-testid="setup-checklist-card">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <ListChecks className="h-4 w-4 text-brand-amber shrink-0" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-brand-amber truncate">
            // Complete your station setup
          </span>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-mono text-[11px] text-ink-primary" data-testid="setup-checklist-pct">{data.pct}%</span>
          <button type="button" onClick={toggle} data-testid="setup-checklist-toggle"
            className="text-ink-muted hover:text-ink-primary transition">
            {collapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </button>
        </div>
      </div>
      <div className="mt-3 h-1.5 rounded-full bg-bg-elevated overflow-hidden">
        <div className="h-full rounded-full bg-brand-amber transition-all duration-500" style={{ width: `${data.pct}%` }} />
      </div>
      {!collapsed && (
        <>
          <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
            {data.items.map((it) => (
              <Link key={it.key} to={it.link} data-testid={`setup-item-${it.key}`}
                className={`flex items-center gap-2 rounded-md px-2.5 py-2 text-xs transition ${
                  it.done ? "text-ink-muted" : "text-ink-primary hover:bg-bg-elevated"}`}>
                {it.done
                  ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-300 shrink-0" />
                  : <Circle className="h-3.5 w-3.5 text-ink-muted shrink-0" />}
                <span className={it.done ? "line-through" : ""}>{it.label}</span>
              </Link>
            ))}
          </div>
          {incomplete && data.pct < 100 && (
            <button type="button" onClick={() => navigate("/onboarding")} data-testid="setup-checklist-resume"
              className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-brand-amber px-4 py-2 text-xs font-semibold text-black hover:bg-brand-amber/90 transition">
              Resume setup wizard <ArrowRight className="h-3.5 w-3.5" />
            </button>
          )}
          {data.pct >= 100 && (
            <div className="mt-4 flex flex-wrap items-center gap-3" data-testid="setup-checklist-complete">
              <span className="text-sm font-bold text-emerald-300">🎉 Setup Complete!</span>
              <button type="button" data-testid="setup-checklist-hide"
                onClick={async () => {
                  try { await api.post("/tour/checklist-visibility", { hidden: true }); } catch { /* noop */ }
                  localStorage.removeItem("bfa_show_checklist");
                  setData((d) => ({ ...d, checklist_hidden: true }));
                }}
                className="text-[11px] font-semibold text-ink-muted hover:text-ink-primary underline">
                Hide checklist
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
